import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { MIN_PASSWORD_LENGTH } from '@wallflower/shared';
import AuthShell, { Field, describeAuthError } from '../components/AuthShell.tsx';
import { accountApi } from '../lib/accountApi.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useDocumentTitle('Create an account');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await accountApi.register(email, password, name.trim() || undefined);
      setSent(true);
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  /**
   * The same screen whether or not the address was already registered. Anything
   * else would turn signup into a way to ask who has an account here.
   */
  if (sent) {
    return (
      <AuthShell
        eyebrow="Check your email"
        title="Confirm your address"
        intro={
          <>
            We've sent a confirmation link to <strong className="text-chalk">{email}</strong>. Open
            it to sign in.
          </>
        }
      >
        <p className="text-sm text-muted">
          Nothing after a minute or two? Check the spam folder, or{' '}
          <button
            type="button"
            className="text-petal hover:underline"
            onClick={() => void accountApi.resendVerification(email).catch(() => undefined)}
          >
            send it again
          </button>
          .
        </p>
        <Link to="/signin" className="btn btn-ghost justify-self-start">
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Create an account"
      title="Set up your wall"
      intro="One account, however many events you run."
    >
      <form onSubmit={onSubmit} className="grid gap-5">
        <Field
          id="signup-name"
          label="Your name"
          hint="Optional. Only used to say hello in emails."
          type="text"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
        />
        <Field
          id="signup-email"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
        <Field
          id="signup-password"
          label="Password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. Length is what matters.`}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={MIN_PASSWORD_LENGTH}
          required
        />

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <button
          className="btn btn-primary justify-self-start"
          disabled={busy || !email || password.length < MIN_PASSWORD_LENGTH}
        >
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </form>

      <p className="text-sm text-muted">
        Already have one?{' '}
        <Link to="/signin" className="text-petal hover:underline">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
